'use client';

import { type FieldIconName, isFieldIconName } from '@zealot/shared/constants/fieldIcons';
import {
  Combobox,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxFilter,
} from '@zealot/ui/components/combobox';
import { bucketByGroup } from '@zealot/ui/lib/bucketByGroup';
import { cn } from '@zealot/ui/lib/utils';
import {
  Activity,
  Award,
  Briefcase,
  Building2,
  Calendar,
  CircleCheck,
  CircleHelp,
  Clock,
  Coins,
  Database,
  FileText,
  Gift,
  type LucideIcon,
  Mail,
  MessageSquare,
  Phone,
  ShieldCheck,
  Tag,
  Target,
  User,
  Users,
} from 'lucide-react';
import * as React from 'react';

type ComboboxSelectItem = {
  label: string;
  value: string;
  disabled?: boolean;
  icon?: string | React.ReactNode;
  group?: string;
};

// gloss
const NAMED_ICONS: Record<FieldIconName, LucideIcon> = {
  activity: Activity,
  award: Award,
  briefcase: Briefcase,
  building: Building2,
  calendar: Calendar,
  'circle-check': CircleCheck,
  'circle-help': CircleHelp,
  clock: Clock,
  coins: Coins,
  database: Database,
  'file-text': FileText,
  gift: Gift,
  mail: Mail,
  'message-square': MessageSquare,
  phone: Phone,
  'shield-check': ShieldCheck,
  tag: Tag,
  target: Target,
  user: User,
  users: Users,
};

// gloss
const ItemIcon = ({ icon, label }: { icon: string | React.ReactNode; label: string }) => {
  if (typeof icon !== 'string') return icon;

  if (icon.startsWith('/')) return <img src={icon} alt={label} className="size-4 shrink-0 object-contain" />;

  const Named = isFieldIconName(icon) ? NAMED_ICONS[icon] : undefined;
  if (Named) return <Named aria-hidden="true" className="size-4 shrink-0" />;

  return <span className="shrink-0">{icon}</span>;
};

const SELECT_TRIGGER_SIZES: Record<'sm' | 'default' | 'lg', string> = {
  sm: 'h-7 px-2 text-xs',
  default: 'h-9 px-3 text-sm',
  lg: 'h-10 px-3 text-sm',
};

// gloss
type ComboboxSelectProps = {
  items: ComboboxSelectItem[];
  value?: string | null;
  defaultValue?: string;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  name?: string;
  showClear?: boolean;
  size?: 'sm' | 'default' | 'lg';
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  itemTextClassName?: string;
  popupClassName?: string;
  trigger?: 'input' | 'button';
  'aria-label'?: string;
};

// gloss
function ComboboxSelect({
  items,
  value: valueProp,
  defaultValue,
  onValueChange,
  placeholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
  name,
  showClear = false,
  size = 'default',
  defaultOpen,
  onOpenChange,
  className,
  itemTextClassName,
  popupClassName,
  trigger = 'input',
  'aria-label': ariaLabel,
}: ComboboxSelectProps) {
  const itemMap = React.useMemo(() => {
    const map = new Map<string, ComboboxSelectItem>();
    for (const item of items) {
      map.set(item.value, item);
    }
    return map;
  }, [items]);

  const hasGroups = items.some((item) => item.group);
  const { contains } = useComboboxFilter();
  const matchesQuery = React.useCallback(
    (item: ComboboxSelectItem, query: string) =>
      contains(item.label, query) || (!!item.group && contains(item.group, query)),
    [contains],
  );
  const groupedItems = React.useMemo(
    () =>
      hasGroups
        ? [...bucketByGroup(items).entries()].map(([group, groupItems]) => ({ value: group, items: groupItems }))
        : null,
    [items, hasGroups],
  );

  // why: must preserve null vs undefined for selectedItem — collapsing null to undefined on clear
  // why: flips the combobox from controlled to uncontrolled
  const selectedItem =
    valueProp === undefined ? undefined : valueProp === null ? null : (itemMap.get(valueProp) ?? null);
  const defaultItem = defaultValue != null ? (itemMap.get(defaultValue) ?? null) : undefined;

  const handleValueChange = React.useCallback(
    (item: ComboboxSelectItem | null) => {
      onValueChange?.(item?.value ?? null);
    },
    [onValueChange],
  );

  return (
    <Combobox
      items={groupedItems ?? items}
      value={selectedItem}
      defaultValue={defaultItem}
      onValueChange={handleValueChange}
      itemToStringValue={(item: ComboboxSelectItem) => item.label}
      filter={hasGroups ? matchesQuery : undefined}
      name={name}
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={(open) => onOpenChange?.(open)}
    >
      {trigger === 'button' ? (
        <div className={cn('relative w-full min-w-0', className)}>
          <ComboboxTrigger
            aria-label={ariaLabel}
            disabled={disabled}
            className={cn(
              'inline-flex w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-lg border border-input bg-field outline-none transition-colors',
              SELECT_TRIGGER_SIZES[size],
              'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30',
              'data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-50',
              showClear && 'pr-9',
              className,
            )}
          >
            <span className="truncate">
              <ComboboxValue placeholder={<span className="text-muted-foreground">{placeholder}</span>} />
            </span>
          </ComboboxTrigger>
          {showClear && <ComboboxClear className="absolute top-1/2 right-1.5 -translate-y-1/2" disabled={disabled} />}
        </div>
      ) : (
        <ComboboxInput
          placeholder={placeholder}
          showClear={showClear}
          size={size}
          disabled={disabled}
          className={cn(className)}
          aria-label={ariaLabel}
          leading={selectedItem?.icon ? <ItemIcon icon={selectedItem.icon} label={selectedItem.label} /> : undefined}
        />
      )}
      <ComboboxContent className={popupClassName}>
        {trigger === 'button' ? (
          <div className="border-border border-b p-1">
            <ComboboxInput
              aria-label={ariaLabel ? `Search ${ariaLabel}` : 'Search'}
              placeholder="Search..."
              disabled={disabled}
              showTrigger={false}
              className="w-full"
            />
          </div>
        ) : null}
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {groupedItems
            ? (group: { value: string; items: ComboboxSelectItem[] }) => (
                <ComboboxGroup key={group.value} items={group.items}>
                  {group.value ? <ComboboxGroupLabel>{group.value}</ComboboxGroupLabel> : null}
                  <ComboboxCollection>
                    {(item: ComboboxSelectItem) => renderItem(item, itemTextClassName)}
                  </ComboboxCollection>
                </ComboboxGroup>
              )
            : (item: ComboboxSelectItem) => renderItem(item, itemTextClassName)}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

const renderItem = (item: ComboboxSelectItem, itemTextClassName?: string) => (
  <ComboboxItem key={item.value} value={item} disabled={item.disabled}>
    <span className="flex items-center gap-2">
      {item.icon ? <ItemIcon icon={item.icon} label={item.label} /> : null}
      <span className={cn(itemTextClassName)}>{item.label}</span>
    </span>
  </ComboboxItem>
);

export type { ComboboxSelectItem, ComboboxSelectProps };
export { ComboboxSelect, ItemIcon };
